import { HeartIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { EmptyState, ErrorState, Flex, LoadingState } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { useReceivedLikes, useSubscription } from '@/hooks';
import { FREE_LIKE_COUNT, type PhotoLike } from '@/services';
import { normalizePhotos } from '@/utils';
import { LikesStatsSection } from './LikesStatsSection';
import { ReceivedLikesList } from './ReceivedLikesList';

function LikesContent({
  likes,
  isLoading,
  isError,
  onRetry,
}: {
  likes: PhotoLike[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const { currentUser } = useCurrentUser();
  const { hasSubscription, presentPaywall } = useSubscription();

  const photosById = useMemo(
    () => new Map(normalizePhotos(currentUser?.photos).map(photo => [photo.id, photo])),
    [currentUser?.photos]
  );

  if (isLoading) return <LoadingState />;
  if (isError) {
    return <ErrorState action={{ label: tCommon('actions.retry'), onClick: onRetry }} />;
  }
  if (likes.length === 0) {
    return (
      <EmptyState
        icon={<HeartIcon size={48} color="gray" />}
        title={t('likes.empty.line1')}
        description={t('likes.empty.line2')}
      />
    );
  }

  const visibleLikes = hasSubscription ? likes : likes.slice(0, FREE_LIKE_COUNT);
  const hiddenCount = Math.max(0, likes.length - visibleLikes.length);
  const onUnlock = () => void presentPaywall().catch(() => {});

  return (
    <ReceivedLikesList
      likes={visibleLikes}
      photosById={photosById}
      hiddenCount={hiddenCount}
      onUnlock={onUnlock}
    />
  );
}

/**
 * Likes tab of the inbox: per-photo stats strip (pro) on top, then the
 * received-likes list capped at FREE_LIKE_COUNT for free users.
 */
export function ReceivedLikesPanel() {
  const { user, currentUser } = useCurrentUser();
  const { hasSubscription, presentPaywall } = useSubscription();

  const myPhotos = useMemo(() => normalizePhotos(currentUser?.photos), [currentUser?.photos]);
  const { likes, likeCountByPhotoId, isLoading, isError, refetch } = useReceivedLikes(
    user,
    myPhotos
  );

  const onUnlock = () => void presentPaywall().catch(() => {});

  return (
    <Flex direction="column" gap="5" pt="3">
      <LikesStatsSection
        photos={myPhotos}
        counts={likeCountByPhotoId}
        hasSubscription={hasSubscription}
        onUnlock={onUnlock}
      />

      <LikesContent likes={likes} isLoading={isLoading} isError={isError} onRetry={refetch} />
    </Flex>
  );
}
